import { useMemo, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { docToPlainText, MAX_FILES_PER_MESSAGE, type RichTextDoc } from '@avslack/shared';
import { channelTitle, sortChannels, useChannels } from '../data/channels';
import { useUsers } from '../data/users';
import { useSendMessage } from '../data/messages';
import type { LocalMessage } from '../data/messages';
import { Dialog, useAnimatedClose } from './Dialog';
import { Avatar } from './Avatar';
import { Icon } from './Icon';

/**
 * Tovabbitas masik csatornaba.
 *
 * A tovabbitott uzenet IDEZETKENT kerul at, forrasmegjelolessel - nem masoljuk
 * el ugy, mintha a tovabbito irta volna. A kiserouzenet nem kotelezo.
 *
 * A MELLEKLETEK is atmennek, ujrafeltoltes nelkul: az uj uzenet ugyanazokra a
 * fajlokra hivatkozik. A kollega fajljat a szerver csak akkor engedi, ha a
 * tovabbito mar most is latja (lasd `assertAttachable`).
 */
export interface ForwardItem {
  message: LocalMessage;
  authorName: string;
}

/**
 * Tovabbitas - egy VAGY tobb uzenet (U5: legfeljebb 5). Tobbnel mindegyik
 * idezet a sajat szerzojevel es idopontjaval all, egymas alatt, egyetlen
 * uzenetben - igy a cel csatornaban egyben olvashato a beszelgetes-reszlet.
 */
export function ForwardDialog({
  items,
  sourceChannelName,
  meId,
  onClose: closeNow,
}: {
  items: ForwardItem[];
  sourceChannelName: string;
  meId: string;
  onClose: () => void;
}) {
  const onClose = useAnimatedClose(closeNow);
  const navigate = useNavigate();
  const { data: channels } = useChannels();
  const { usersById, nameDisplay } = useUsers();
  const [targetId, setTargetId] = useState<string>('');
  const [note, setNote] = useState('');
  const [sending, setSending] = useState(false);

  const send = useSendMessage(targetId || undefined);

  const options = useMemo(
    () => sortChannels(channels ?? []).filter((c) => !c.isArchived && c.membership.isMember),
    [channels],
  );

  const forward = async () => {
    if (!targetId || sending) return;
    setSending(true);

    const many = items.length > 1;
    const quoteOf = (message: LocalMessage) =>
      message.text.trim()
        ? [
            {
              type: 'blockquote' as const,
              content: message.text.split('\n').map((line) => ({
                type: 'paragraph' as const,
                ...(line ? { content: [{ type: 'text' as const, text: line }] } : {}),
              })),
            },
          ]
        : [];

    const quoted: RichTextDoc = {
      type: 'doc',
      content: [
        ...(note.trim()
          ? [
              {
                type: 'paragraph' as const,
                content: [{ type: 'text' as const, text: note.trim() }],
              },
            ]
          : []),
        {
          type: 'paragraph',
          content: [
            {
              type: 'text',
              text: many
                ? `Továbbítva innen: ${sourceChannelName} (${items.length} üzenet)`
                : `Továbbítva innen: ${sourceChannelName} — ${items[0]!.authorName}`,
              marks: [{ type: 'italic' as const }],
            },
          ],
        },
        // Tobbnel mindegyik ele a szerzo es az idopont; egynel marad a regi alak.
        ...items.flatMap(({ message, authorName }) => [
          ...(many
            ? [
                {
                  type: 'paragraph' as const,
                  content: [
                    {
                      type: 'text' as const,
                      text: `${authorName} · ${new Date(message.createdAt).toLocaleString('hu-HU', {
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}`,
                      marks: [{ type: 'bold' as const }],
                    },
                  ],
                },
              ]
            : []),
          // Csak-kepes uzenetnel nincs mit idezni: ures idezet-dobozt nem teszunk.
          ...quoteOf(message),
        ]),
      ],
    };
    // A csatolmanyok is atkerulnek - az uzenetenkenti felso hataron belul.
    const fileIds = items
      .flatMap(({ message }) => message.files.map((file) => file.id))
      .slice(0, MAX_FILES_PER_MESSAGE);

    try {
      /*
        A sima szoveg a DOKUMENTUMBOL keszul, nem az eredeti szovegebol.

        Csak-kepes uzenetnel az eredeti szoveg ures, es a kuldes ures szoveg
        mellett eldobna a dokumentumot - vele a "Tovabbitva innen" sort is.
      */
      await send({ text: docToPlainText(quoted), body: quoted }, meId, null, fileIds);
      onClose();
      void navigate({ to: '/c/$channelId', params: { channelId: targetId } });
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog
      open
      onOpenChange={(next) => !next && onClose()}
      title={items.length > 1 ? `${items.length} üzenet továbbítása` : 'Továbbítás'}
      description={
        items.length > 1
          ? 'Az üzenetek idézetként, egyben kerülnek át, a szerzőkkel és a forrással.'
          : 'Az üzenet idézetként kerül át, forrásmegjelöléssel.'
      }
    >
      <div className="mt-5">
        <p id="forward-target-label" className="mb-1.5 text-sm font-semibold">
          Cél csatorna
        </p>
        <ul
          aria-labelledby="forward-target-label"
          className="max-h-56 overflow-y-auto overscroll-contain rounded-control border border-border-soft"
        >
          {options.length === 0 && (
            <li className="px-3 py-4 text-center text-sm text-ink-muted">
              {channels ? 'Nincs csatorna, ahová továbbíthatnád.' : 'Betöltés…'}
            </li>
          )}
          {options.map((channel) => {
            const isActive = channel.id === targetId;
            const other = (channel.memberIds ?? []).find((id) => id !== meId);
            return (
              <li key={channel.id}>
                <button
                  type="button"
                  onClick={() => setTargetId(channel.id)}
                  aria-pressed={isActive}
                  className={[
                    'flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm',
                    isActive ? 'bg-accent-soft font-semibold' : 'hover:bg-surface-hover',
                  ].join(' ')}
                >
                  {other ? (
                    <Avatar
                      userId={other}
                      name={channelTitle(channel, usersById, meId, nameDisplay)}
                      size="sm"
                    />
                  ) : (
                    <span
                      aria-hidden="true"
                      className="grid size-6 place-items-center text-ink-faint"
                    >
                      <Icon name={channel.kind === 'private' ? 'lakat' : 'csatorna'} size="sm" />
                    </span>
                  )}
                  <span className="truncate">
                    {channelTitle(channel, usersById, meId, nameDisplay)}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>

        <label className="mb-1.5 mt-4 block text-sm font-semibold" htmlFor="forward-note">
          Kísérőüzenet <span className="font-normal text-ink-faint">nem kötelező</span>
        </label>
        <input
          id="forward-note"
          autoComplete="off"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Miért küldöd tovább?"
          className="w-full rounded-control border border-border-soft bg-surface px-3 py-2 text-base outline-none placeholder:text-ink-faint focus:border-accent"
        />

        <div className="mt-6 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-control px-4 py-2 text-sm font-medium text-ink-muted transition-colors duration-(--duration-quick) hover:bg-surface-hover hover:text-ink"
          >
            Mégse
          </button>
          <button
            type="button"
            onClick={() => void forward()}
            disabled={!targetId || sending}
            className="rounded-control bg-accent px-4 py-2 text-sm font-semibold text-accent-ink transition-colors duration-(--duration-quick) hover:bg-accent-hover disabled:opacity-40"
          >
            {sending ? 'Küldés…' : 'Továbbítás'}
          </button>
        </div>
      </div>
    </Dialog>
  );
}
